import { useMutation } from "@apollo/client";
import {
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminEditUserAttributeMutation,
  InternalAdminEditUserAttributeMutationVariables,
  InternalAdminUserAttributeFragment,
} from "gql/graphql";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import colors from "app/colors";
import {
  CancelIcon,
  CheckIcon,
  CircleMinusIcon,
  CirclePlusIcon,
  CloseIcon,
  PencilOutlineIcon,
} from "modules/icons";
import { FieldValues, useForm } from "react-hook-form";
import { getMessageFromError } from "modules/utils/Functions";
import { isEmpty } from "lodash";
import useIsHovering from "modules/hooks/use-is-hovering";
import useGlobalEditing from "modules/hooks/use-global-editing";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminUserAttributeList from "../hooks/use-internal-admin-user-attribute-list";
import INTERNAL_ADMIN_EDIT_USER_ATTRIBUTE_MUTATION from "../_networking/internal-admin-edit-user-attribute";

interface IProps {
  userAttribute: InternalAdminUserAttributeFragment;
}

const InternalAdminEditUserAttribute = ({ userAttribute }: IProps) => {
  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();
  const { qsOrganizationId } = useQueryString();

  const { mode, setMode } = useGlobalEditing(
    `InternalAdminEditUserAttribute-${userAttribute.id}`,
  );

  const { userAttributes } = useInternalAdminUserAttributeList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
      userAttributeCategoryId: userAttribute.userAttributeCategoryId,
    },
  });

  const [internalAdminEditUserAttributeMutationAPI, { loading }] = useMutation<
    InternalAdminEditUserAttributeMutation,
    InternalAdminEditUserAttributeMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_ATTRIBUTE_MUTATION);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: userAttribute.name || "",
    },
  });

  const onClearClicked = useCallback(() => {
    setValue("name", "");
  }, [setValue]);

  const name = watch("name");

  const isDuplicateName = useMemo(() => {
    if (!userAttribute.userAttributeCategoryId) return false;
    const lowerCaseName = name.toLowerCase();
    return (
      !isEmpty(name) &&
      userAttributes.some(
        (_userAttribute) =>
          _userAttribute.name.toLowerCase() === lowerCaseName &&
          _userAttribute.id !== userAttribute.id,
      )
    );
  }, [
    name,
    userAttribute.id,
    userAttribute.userAttributeCategoryId,
    userAttributes,
  ]);

  useEffect(() => {
    if (isDuplicateName) {
      setError("name", {
        message: "This name is already taken",
      });
    } else {
      clearErrors("name");
    }
  }, [clearErrors, isDuplicateName, setError]);

  const onSubmit = useCallback(
    async ({ name }: FieldValues) => {
      if (!qsOrganizationId) return;
      internalAdminEditUserAttributeMutationAPI({
        variables: {
          organizationId: qsOrganizationId,
          id: userAttribute.id,
          input: {
            name,
          },
        },
        onError: (error) => {
          setError("name", {
            message: error.message,
          });
        },
        onCompleted: () => {
          setMode("display")();
        },
      });
    },
    [
      internalAdminEditUserAttributeMutationAPI,
      qsOrganizationId,
      setError,
      setMode,
      userAttribute.id,
    ],
  );

  const onArchiveClicked = useCallback(() => {
    if (!qsOrganizationId) return;
    internalAdminEditUserAttributeMutationAPI({
      variables: {
        organizationId: qsOrganizationId,
        id: userAttribute.id,
        input: {
          active: !userAttribute.active,
        },
      },
      onError: (error) => {
        setError("name", {
          message: error.message,
        });
      },
      onCompleted: () => {
        setMode("display")();
      },
    });
  }, [
    internalAdminEditUserAttributeMutationAPI,
    qsOrganizationId,
    setError,
    setMode,
    userAttribute.active,
    userAttribute.id,
  ]);

  if (mode === "edit") {
    return (
      <form
        id="admin-edit-user-attribute-name-form"
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        <FormControl isInvalid={!!errors?.name} w="full">
          <Stack w="full" spacing={0}>
            <HStack w="full" spacing={1}>
              <InputGroup fontWeight={400} color={colors.gray50} fontSize={14}>
                <Input
                  id="name"
                  size="sm"
                  fontSize={14}
                  fontWeight={600}
                  color={colors.gray50}
                  borderRadius={4}
                  borderWidth={2}
                  pl={5}
                  borderColor={colors.blue}
                  {...register("name")}
                />
                <InputRightElement h="100%" w={7}>
                  <IconButton
                    aria-label="clear name"
                    icon={<CancelIcon boxSize={5} fill={colors.gray40} />}
                    variant="ghost"
                    size="xxs"
                    onClick={onClearClicked}
                  />
                </InputRightElement>
              </InputGroup>
              <IconButton
                aria-label="save"
                type="submit"
                icon={
                  loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <CheckIcon boxSize={4} fill={colors.white} />
                  )
                }
                bg={colors.blue}
                variant="hover"
                size="xs"
                onClick={setMode("edit")}
                isDisabled={loading || isDuplicateName}
              />
              <IconButton
                aria-label="cancel"
                icon={<CloseIcon boxSize={4} fill={colors.gray40} />}
                borderColor={colors.gray40}
                variant="outline"
                size="xs"
                onClick={setMode("display")}
                isDisabled={loading}
              />
            </HStack>
            <FormErrorMessage m={0}>
              {getMessageFromError(errors?.name?.message)}
            </FormErrorMessage>
          </Stack>
        </FormControl>
      </form>
    );
  }

  if (userAttribute.active === false) {
    return (
      <HStack
        w="full"
        spacing={4}
        borderRadius={4}
        py="6px"
        px={1}
        onMouseOut={onMouseOut}
        onMouseOver={onMouseOver}
        _hover={{
          backgroundColor: colors.blue20,
        }}
      >
        <Text
          fontSize={14}
          fontWeight={500}
          color={colors.grayMain}
          fontStyle="italic"
          w="full"
        >
          {userAttribute.name}
        </Text>
        <IconButton
          aria-label="restore user attribute"
          icon={<CirclePlusIcon boxSize={5} fill={colors.blue} />}
          variant="ghost"
          size="xxs"
          onClick={onArchiveClicked}
          visibility={isHovering ? "visible" : "hidden"}
        />
      </HStack>
    );
  }

  return (
    <HStack
      w="full"
      spacing={4}
      borderRadius={4}
      py="6px"
      px={1}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      _hover={{
        backgroundColor: colors.blue20,
      }}
    >
      <Text fontSize={14} fontWeight={600} color={colors.gray50} w="full">
        {userAttribute.name}
      </Text>
      <IconButton
        aria-label="Edit user attribute name"
        icon={<PencilOutlineIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={setMode("edit")}
        visibility={isHovering ? "visible" : "hidden"}
      />
      <IconButton
        aria-label="archive user attribute"
        icon={<CircleMinusIcon boxSize={5} fill={colors.blue} />}
        variant="ghost"
        size="xxs"
        onClick={onArchiveClicked}
        visibility={isHovering ? "visible" : "hidden"}
      />
    </HStack>
  );
};

export default memo(InternalAdminEditUserAttribute);
